import {
  createGlobalTheme,
  createTheme,
  createThemeContract,
} from "@vanilla-extract/css";
import { darkColorMap, lightColorMap } from "./colors";
import { bodyFontSizes } from "./typography";

const border: Record<string, string | string[]> = {
  // can't use numbers here because it's outside of vanilla-extract
  widths: ["1px", "2px"],
  style: "solid",
};

export const colors = createThemeContract({
  bgColor: "",
  primary: "",
  onPrimary: "",
  onPrimaryOpacityLow: "",
  onPrimaryOpacityMid: "",
  onPrimaryOpacityHigh: "",
  primaryFixed: "",
  onPrimaryFixed: "",
  primaryContainer: "",
  onPrimaryContainer: "",
  disabled: "",
  onDisabled: "",
  white: "",
  tonal: "",
  glass: "",
  onGlass: "",
  plBlue: "",
  plLilac: "",
  surface: "",
  surfaceVariant: "",
  onSurface: "",
  onSurfaceFixed: "",
  onSurfaceOpacityLow: "",
  onSurfaceOpacityMid: "",
  onSurfaceOpacityHigh: "",
  onSurfaceVariant: "",
  surfaceContainer: "",
  surfaceBright: "",
  surfaceFixed: "",
  error: "",
  onError: "",
  errorContainer: "",
  success: "",
  onSuccess: "",
  outline: "",
  outlineVariant: "",
  focused: "",
  hovered: "",
  pitchGreen: "",
  overlay: "",
  scrim: "",
  inverseSurface: "",
  inverseOnSurface: "",
  copnr: {
    0: {
      backgroundColor: "",
      color: "",
    },
    25: {
      backgroundColor: "",
      color: "",
    },
    50: {
      backgroundColor: "",
      color: "",
    },
    75: {
      backgroundColor: "",
      color: "",
    },
  },
  difficulties: {
    1: {
      backgroundColor: "",
      color: "",
    },
    2: {
      backgroundColor: "",
      color: "",
    },
    3: {
      backgroundColor: "",
      color: "",
    },
    4: {
      backgroundColor: "",
      color: "",
    },
    5: {
      backgroundColor: "",
      color: "",
    },
  },
  teams: {
    1: {
      backgroundColor: "",
      color: "",
    },
    3: {
      backgroundColor: "",
      color: "",
    },
    4: {
      backgroundColor: "",
      color: "",
    },
    6: {
      backgroundColor: "",
      color: "",
    },
    7: {
      backgroundColor: "",
      color: "",
    },
    8: {
      backgroundColor: "",
      color: "",
    },
    9: {
      backgroundColor: "",
      color: "",
    },
    11: {
      backgroundColor: "",
      color: "",
    },
    13: {
      backgroundColor: "",
      color: "",
    },
    14: {
      backgroundColor: "",
      color: "",
    },
    17: {
      backgroundColor: "",
      color: "",
    },
    21: {
      backgroundColor: "",
      color: "",
    },
    31: {
      backgroundColor: "",
      color: "",
    },
    36: {
      backgroundColor: "",
      color: "",
    },
    39: {
      backgroundColor: "",
      color: "",
    },
    43: {
      backgroundColor: "",
      color: "",
    },
    54: {
      backgroundColor: "",
      color: "",
    },
    90: {
      backgroundColor: "",
      color: "",
    },
    91: {
      backgroundColor: "",
      color: "",
    },
    94: {
      backgroundColor: "",
      color: "",
    },
  },
  result: {
    w: "",
    d: "",
    l: "",
  },
  liveMatchGradient: "",
  blueBrandGradient: "",
  blueLilacGradient: "",
  liveMatchGradientRtl: "",
  blueBrandGradientRtl: "",
  blueLilacGradientRtl: "",
  challengeGreen: "",
});

export const headingFontSizeVars = createThemeContract({
  "6xl": null,
  "5xl": null,
  "4xl": null,
  "3xl": null,
  "2xl": null,
  xl: null,
  l: null,
  m: null,
  s: null,
  xs: null,
  "2xs": null,
  "3xs": null,
});

// Base global theme is scoped to .ism, which must be nested inside the active theme (light/dark)
// this ensures colors are defined before global tokens reference them
export const base = createGlobalTheme(".ism", {
  // TODO: These are a guess for now. TBD with design team.
  borders: {
    outline: `${border.widths[0]} ${border.style} ${colors.outline}`,
    outlineVariant: `${border.widths[0]} ${border.style} ${colors.outlineVariant}`,
    error: `${border.widths[1]} ${border.style} ${colors.error}`,
    disabled: `${border.widths[0]} ${border.style} ${colors.disabled}`,
  },
  blur: {
    2: "blur(2px)",
    5: "blur(5px)",
    10: "blur(10px)",
  },
  fontSizes: {
    heading: headingFontSizeVars,
    body: bodyFontSizes,
  },
  fonts: {
    bold: '"PremierSans-Bold", Arial, "Helvetica Neue", Helvetica, "sans-seriff"',
    boldItalic:
      '"PremierSans-Bold-Italic", Arial, "Helvetica Neue", Helvetica, "sans-seriff"',
    italic:
      '"PremierSans-Italic", Arial, "Helvetica Neue", Helvetica, "sans-seriff"',
    light:
      '"PremierSans-Light", Arial, "Helvetica Neue", Helvetica, "sans-serif"',
    regular:
      '"PremierSans-Regular", Arial, "Helvetica Neue", Helvetica, "sans-serif"',
  },
  radii: {
    none: "0",
    /** 4px */
    xs: "4px",
    /** 8px */
    s: "8px",
    /** 12px */
    m: "12px",
    /** 16px */
    l: "16px",
    /** 24px */
    xl: "24px",
    full: "50%",
  },
  space: {
    none: "0",
    /** 0.4rem */
    "2xs": "0.4rem",
    /** 0.8rem */
    xs: "0.8rem",
    /** 1.2rem */
    s: "1.2rem",
    /** 1.6rem */
    m: "1.6rem",
    /** 2.4rem */
    l: "2.4rem",
    /** 3.2rem */
    xl: "3.2rem",
    /** 4rem */
    "2xl": "4rem",
    /** 4.8rem */
    "3xl": "4.8rem",
    /** 5.6rem */
    "4xl": "5.6rem",
    /** 6.4rem */
    "5xl": "6.4rem",
  },
  zIndex: {
    base: "0",
    table: { stickyCol: "5", stickyHead: "10", firstStickyCol: "15" },
    actionBar: "25",
    stickyControl: "30", // floating page controls eg. contextual help pages
    modal: "605", // keep our modals above pl header 600, but below adobe modals 700
  },
});

export const lightTheme = createTheme(colors, lightColorMap);
export const darkTheme = createTheme(colors, darkColorMap);

export const vars = { ...base, colors };

export const fontVariantsMap = {
  "6xl": { fontSize: vars.fontSizes.heading["6xl"] },
  "5xl": { fontSize: vars.fontSizes.heading["5xl"] },
  "4xl": { fontSize: vars.fontSizes.heading["4xl"] },
  "3xl": { fontSize: vars.fontSizes.heading["3xl"] },
  "2xl": { fontSize: vars.fontSizes.heading["2xl"] },
  xl: { fontSize: vars.fontSizes.heading.xl },
  l: { fontSize: vars.fontSizes.heading.l },
  m: { fontSize: vars.fontSizes.heading.m },
  s: { fontSize: vars.fontSizes.heading.s },
  xs: { fontSize: vars.fontSizes.heading.xs },
  "2xs": { fontSize: vars.fontSizes.heading["2xs"] },
  "3xs": { fontSize: vars.fontSizes.heading["3xs"] },
};
